import { Container, Sprite } from "pixi.js";
import type {
  ProjectileSnapshot,
  ShipSnapshot,
} from "@frontier/protocol/contracts";
import type { ShipCombatImpact } from "./combat-vfx-presentation";
import { combatPointVisible, type CombatViewport } from "./combat-vfx";
import type { RenderedCombatShips } from "./combat-ship-pose";
import { shieldTexture } from "./shield-textures";

export const SHIELD_BUBBLE_LIMIT = 48;
export const SHIELD_REACTION_MS = 440;

interface ShieldView {
  container: Container;
  shell: Sprite;
  impact: Sprite;
  ripple: Sprite;
  owner: ShipSnapshot;
  hitAt: number;
  waveAt: number;
  incomingAngle: number | null;
}

/** A visible approach estimates the lit side, never whether damage occurred. */
export function incomingShieldAngle(
  ship: ShipSnapshot,
  shots: readonly ProjectileSnapshot[],
): number | null {
  let closest = Math.max(90, ship.radius + 70);
  let angle: number | null = null;
  for (const shot of shots) {
    if (shot.ownerId === ship.id || shot.kind === "mine") continue;
    const dx = ship.x - shot.x;
    const dy = ship.y - shot.y;
    const distance = Math.hypot(dx, dy);
    if (distance > closest || distance < 1) continue;
    const alignment =
      (dx * Math.cos(shot.angle) + dy * Math.sin(shot.angle)) / distance;
    if (alignment < 0.8) continue;
    closest = distance;
    angle = shot.angle + Math.PI;
  }
  return angle;
}

function sameShip(old: ShipSnapshot, ship: ShipSnapshot): boolean {
  return (
    !ship.dead &&
    ship.shield > 0 &&
    !ship.docked &&
    old.hullId === ship.hullId &&
    old.sectorId === ship.sectorId &&
    old.maxHp === ship.maxHp &&
    old.maxShield === ship.maxShield &&
    (old.controls?.flightId ?? null) === (ship.controls?.flightId ?? null) &&
    Math.hypot(ship.x - old.x, ship.y - old.y) <= 300
  );
}

export class ShieldBubbles {
  readonly container = new Container({ label: "shield-bubbles" });
  private readonly shellTexture = shieldTexture("shell");
  private readonly impactTexture = shieldTexture("impact");
  private readonly rippleTexture = shieldTexture("ripple");
  private readonly views: ShieldView[] = [];
  private ships: ReadonlyMap<string, ShipSnapshot> = new Map();
  private receivedAt = 0;

  receive(
    impacts: readonly ShipCombatImpact[],
    ships: ReadonlyMap<string, ShipSnapshot>,
    shots: readonly ProjectileSnapshot[],
    now: number,
  ): void {
    this.ships = ships;
    this.receivedAt = now;
    for (const view of this.views) {
      const ship = ships.get(view.owner.id);
      if (!ship || !sameShip(view.owner, ship)) {
        view.hitAt = -Infinity;
        view.container.visible = false;
      } else view.owner = ship;
    }
    for (const impact of impacts) {
      if (!impact.shieldHit) continue;
      const ship = ships.get(impact.shipId);
      if (!ship || ship.dead || ship.docked || ship.shield <= 0) continue;
      const view =
        this.views.find(
          (value) =>
            value.owner.id === ship.id &&
            now - value.hitAt < SHIELD_REACTION_MS,
        ) ?? this.acquire(ship, now);
      if (!view) continue;
      if (now - view.hitAt >= SHIELD_REACTION_MS || now - view.waveAt >= 220)
        view.waveAt = now;
      view.owner = ship;
      view.hitAt = now;
      view.incomingAngle = incomingShieldAngle(ship, shots);
      view.container.label = `shield-bubble:${ship.id}`;
    }
  }

  render(
    now: number,
    viewport?: CombatViewport,
    reducedMotion = false,
    renderedShips?: RenderedCombatShips,
  ): void {
    const freshness = Math.max(
      0,
      1 - Math.max(0, now - this.receivedAt - 250) / 120,
    );
    for (const view of this.views) {
      const ship = this.ships.get(view.owner.id);
      const pose = renderedShips?.get(view.owner.id);
      const point = renderedShips ? pose?.container : ship;
      const age = (now - view.hitAt) / SHIELD_REACTION_MS;
      const radius =
        pose?.shieldRadius ?? Math.max(16, view.owner.radius * 1.45);
      view.container.visible =
        !!ship &&
        !!point &&
        age >= 0 &&
        age < 1 &&
        freshness > 0 &&
        combatPointVisible(point, radius * 1.1, viewport);
      if (!view.container.visible || !point) continue;
      view.container.position.set(point.x, point.y);
      view.container.alpha = (1 - age) ** 1.3 * freshness;
      view.shell.width = view.shell.height = radius * 2;
      view.shell.alpha = 0.85;
      view.impact.width = view.impact.height = radius * 2;
      view.impact.visible = view.incomingAngle !== null;
      view.impact.rotation = view.incomingAngle ?? 0;
      view.impact.alpha = 0.9;
      const waveAge = (now - view.waveAt) / SHIELD_REACTION_MS;
      view.ripple.visible = !reducedMotion && waveAge >= 0 && waveAge < 1;
      view.ripple.width = view.ripple.height =
        radius * 2 * (0.4 + Math.min(1, waveAge) * 0.7);
      view.ripple.alpha = (1 - Math.min(1, waveAge)) * 0.55;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.shellTexture.destroy(true);
    this.impactTexture.destroy(true);
    this.rippleTexture.destroy(true);
    this.views.length = 0;
    this.ships = new Map();
  }

  private acquire(ship: ShipSnapshot, now: number): ShieldView | undefined {
    const spare = this.views.find(
      (view) => now - view.hitAt >= SHIELD_REACTION_MS,
    );
    if (spare) return spare;
    if (this.views.length >= SHIELD_BUBBLE_LIMIT) return undefined;
    const container = new Container();
    const shell = new Sprite({
      texture: this.shellTexture,
      anchor: 0.5,
      blendMode: "add",
      label: "shield-shell",
    });
    const impact = new Sprite({
      texture: this.impactTexture,
      anchor: 0.5,
      blendMode: "add",
      label: "shield-flare",
    });
    const ripple = new Sprite({
      texture: this.rippleTexture,
      anchor: 0.5,
      blendMode: "add",
      label: "shield-ripple",
    });
    container.addChild(shell, ripple, impact);
    this.container.addChild(container);
    const view: ShieldView = {
      container,
      shell,
      impact,
      ripple,
      owner: ship,
      hitAt: -Infinity,
      waveAt: now,
      incomingAngle: null,
    };
    this.views.push(view);
    return view;
  }
}
